/**
 * VideoAdPlayer - Handles pre-roll and mid-roll video ads for all streams
 * Wraps around the main video content and shows ads before/during playback
 * 
 * Pre-roll: Replaces content until ad finishes (content not yet visible)
 * Mid-roll: Overlays ad ON TOP of content (content stays mounted, avoids iframe restart)
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { X, Volume2, VolumeX, ExternalLink } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const VideoAdPlayer = ({ 
  children, 
  channelId = null,
  onAdStart,
  onAdEnd,
  autoPlayAd = true,
  showMidRollAfter = 1500,
  midRollIntervalMin = 1500,
  midRollIntervalMax = 2700,
}) => {
  const [currentAd, setCurrentAd] = useState(null);
  const [adType, setAdType] = useState(null);
  const [isAdPlaying, setIsAdPlaying] = useState(false);
  const [adTimeRemaining, setAdTimeRemaining] = useState(0);
  const [canSkip, setCanSkip] = useState(false);
  const [adMuted, setAdMuted] = useState(false);
  const [adLoading, setAdLoading] = useState(true);
  const [preRollComplete, setPreRollComplete] = useState(false);
  
  const adVideoRef = useRef(null);
  const contentWatchTime = useRef(0);
  const lastMidRollTime = useRef(0);
  const watchIntervalRef = useRef(null);
  
  // Fetch pre-roll ad on mount
  useEffect(() => {
    const fetchPreRollAd = async () => {
      try {
        const params = channelId ? `?channel_id=${channelId}` : '';
        const response = await axios.get(`${API}/video-ads/play/pre_roll${params}`);
        if (response.data) {
          setCurrentAd(response.data);
          setAdType('pre_roll');
          setIsAdPlaying(true);
          setAdTimeRemaining(response.data.duration_seconds || 15);
          onAdStart?.('pre_roll');
        } else {
          setPreRollComplete(true);
        }
      } catch (error) {
        console.log('No pre-roll ads available');
        setPreRollComplete(true);
      } finally {
        setAdLoading(false);
      }
    };
    
    fetchPreRollAd();
  }, [channelId]);
  
  // Fetch mid-roll ad
  const fetchMidRollAd = useCallback(async () => {
    try {
      const params = channelId ? `?channel_id=${channelId}` : '';
      const response = await axios.get(`${API}/video-ads/play/mid_roll${params}`);
      if (response.data) {
        setCurrentAd(response.data);
        setAdType('mid_roll');
        setIsAdPlaying(true);
        setAdTimeRemaining(response.data.duration_seconds || 15);
        setCanSkip(false);
        lastMidRollTime.current = contentWatchTime.current;
        onAdStart?.('mid_roll');
      }
    } catch (error) {
      console.log('No mid-roll ads available');
    }
  }, [channelId, onAdStart]);
  
  // Random interval target for next mid-roll
  const nextMidRollTarget = useRef(0);
  
  // Track content watch time for mid-roll ads
  useEffect(() => {
    if (preRollComplete && !isAdPlaying) {
      // Set initial random target if not set
      if (nextMidRollTarget.current === 0) {
        nextMidRollTarget.current = showMidRollAfter + Math.floor(Math.random() * (midRollIntervalMax - midRollIntervalMin));
      }
      
      watchIntervalRef.current = setInterval(() => {
        contentWatchTime.current += 1;
        
        const timeSinceLastMidRoll = contentWatchTime.current - lastMidRollTime.current;
        const shouldShowMidRoll = timeSinceLastMidRoll >= nextMidRollTarget.current;
          
        if (shouldShowMidRoll) {
          // Set next random interval (25-45 min)
          nextMidRollTarget.current = midRollIntervalMin + Math.floor(Math.random() * (midRollIntervalMax - midRollIntervalMin));
          fetchMidRollAd();
        }
      }, 1000);
    }
    
    return () => {
      if (watchIntervalRef.current) {
        clearInterval(watchIntervalRef.current);
      }
    };
  }, [preRollComplete, isAdPlaying, showMidRollAfter, midRollIntervalMin, midRollIntervalMax, fetchMidRollAd]);
  
  // Ad video playback handling
  useEffect(() => {
    if (!currentAd) return;
    
    // Billboard (image) ads use a timer instead of video events
    if (currentAd.ad_format === 'billboard') {
      const duration = currentAd.duration_seconds || 10;
      setAdTimeRemaining(duration);
      let remaining = duration;
      
      const timer = setInterval(() => {
        remaining -= 1;
        setAdTimeRemaining(Math.max(0, remaining));
        
        if (currentAd.is_skippable && (duration - remaining) >= (currentAd.skip_after_seconds || 3)) {
          setCanSkip(true);
        }
        
        if (remaining <= 0) {
          clearInterval(timer);
          trackAdEvent('complete');
          finishAd();
        }
      }, 1000);
      
      return () => clearInterval(timer);
    }
    
    // Video ads use video element events
    if (!adVideoRef.current) return;
    
    const video = adVideoRef.current;
    
    const handleTimeUpdate = () => {
      const remaining = Math.ceil(video.duration - video.currentTime);
      setAdTimeRemaining(remaining > 0 ? remaining : 0);
      
      if (currentAd.is_skippable && video.currentTime >= (currentAd.skip_after_seconds || 5)) {
        setCanSkip(true);
      }
    };
    
    const handleEnded = () => {
      trackAdEvent('complete');
      finishAd();
    };
    
    const handleError = () => {
      finishAd();
    };
    
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('error', handleError);
    
    if (autoPlayAd) {
      video.play().catch(() => {
        video.muted = true;
        setAdMuted(true);
        video.play().catch(() => {});
      });
    }
    
    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('error', handleError);
    };
  }, [currentAd, autoPlayAd]);
  
  const trackAdEvent = async (eventType) => {
    if (!currentAd) return;
    try {
      await axios.post(`${API}/video-ads/track/${eventType}/${currentAd.id}`);
    } catch (error) {
      // silent
    }
  };
  
  const handleAdClick = () => {
    if (currentAd?.click_url) {
      trackAdEvent('click');
      window.open(currentAd.click_url, '_blank');
    }
  };
  
  const skipAd = () => {
    if (canSkip) {
      trackAdEvent('skip');
      finishAd();
    }
  };
  
  const finishAd = () => {
    setIsAdPlaying(false);
    setCurrentAd(null);
    setCanSkip(false);
    
    if (adType === 'pre_roll') {
      setPreRollComplete(true);
    }
    
    onAdEnd?.(adType);
    setAdType(null);
  };
  
  const toggleAdMute = () => {
    if (adVideoRef.current) {
      adVideoRef.current.muted = !adVideoRef.current.muted;
      setAdMuted(adVideoRef.current.muted);
    }
  };
  
  const getAdVideoUrl = (ad) => {
    if (!ad?.video_url) return null;
    if (ad.video_url.startsWith('http')) return ad.video_url;
    if (ad.video_url.startsWith('/api')) {
      return `${process.env.REACT_APP_BACKEND_URL}${ad.video_url}`;
    }
    return ad.video_url;
  };

  // Ad overlay UI component (reused for both pre-roll and mid-roll)
  const AdOverlayUI = () => {
    if (!currentAd) return null;
    const videoUrl = getAdVideoUrl(currentAd);
    const isBillboard = currentAd.ad_format === 'billboard' && currentAd.image_url;
    const imageUrl = isBillboard
      ? (currentAd.image_url.startsWith('/api')
        ? `${process.env.REACT_APP_BACKEND_URL}${currentAd.image_url}`
        : currentAd.image_url)
      : null;
    
    return (
      <div className="absolute inset-0 z-50 bg-black" data-testid="video-ad-player">
        {isBillboard ? (
          <img
            src={imageUrl}
            alt={currentAd.sponsor_name || 'Ad'}
            className="w-full h-full object-contain cursor-pointer"
            onClick={handleAdClick}
          />
        ) : videoUrl ? (
          <video
            ref={adVideoRef}
            src={videoUrl}
            className="w-full h-full object-contain cursor-pointer"
            onClick={handleAdClick}
            playsInline
            muted={adMuted}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <p className="text-white">Ad loading...</p>
          </div>
        )}
        
        {/* Ad Overlay UI */}
        <div className="absolute inset-0 pointer-events-none">
          {/* Top Bar */}
          <div className="absolute top-0 left-0 right-0 flex items-center justify-between p-3 bg-gradient-to-b from-black/60 to-transparent pointer-events-auto">
            <div className="flex items-center gap-2">
              <span className="bg-yellow-500 text-black text-[10px] font-bold px-2 py-0.5 uppercase tracking-wider">
                Ad
              </span>
              {currentAd.sponsor_name && (
                <span className="text-white/70 text-xs">{currentAd.sponsor_name}</span>
              )}
            </div>
            {!isBillboard && (
              <button onClick={toggleAdMute} className="p-2 hover:bg-white/10 rounded transition-colors">
                {adMuted ? <VolumeX className="w-5 h-5 text-white" /> : <Volume2 className="w-5 h-5 text-white" />}
              </button>
            )}
          </div>
          
          {/* Bottom Bar */}
          <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between p-3 bg-gradient-to-t from-black/60 to-transparent pointer-events-auto">
            <div className="text-white text-sm">
              {adTimeRemaining > 0 && (
                <span className="bg-black/50 px-2 py-1 rounded">Ad ends in {adTimeRemaining}s</span>
              )}
            </div>
            <div>
              {currentAd.is_skippable ? (
                canSkip ? (
                  <button
                    onClick={skipAd}
                    className="flex items-center gap-2 bg-white/90 hover:bg-white text-black px-4 py-2 text-sm font-bold uppercase tracking-wider transition-colors"
                    data-testid="skip-ad-btn"
                  >
                    Skip Ad <X className="w-4 h-4" />
                  </button>
                ) : (
                  <div className="bg-black/70 text-white px-4 py-2 text-sm">
                    Skip in {Math.max(0, (currentAd.skip_after_seconds || 5) - Math.floor((currentAd.duration_seconds || 15) - adTimeRemaining))}s
                  </div>
                )
              ) : (
                <div className="bg-black/70 text-white px-4 py-2 text-sm">Ad &bull; {adTimeRemaining}s</div>
              )}
            </div>
          </div>
          
          {currentAd.click_url && (
            <button
              onClick={handleAdClick}
              className="absolute bottom-16 right-3 flex items-center gap-1 bg-black/70 hover:bg-black/90 text-white px-3 py-1.5 text-xs transition-colors pointer-events-auto"
            >
              <ExternalLink className="w-3 h-3" /> Learn More
            </button>
          )}
        </div>
        
        {/* Progress Bar */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20">
          <div 
            className="h-full bg-yellow-500 transition-all duration-100"
            style={{ width: `${((currentAd.duration_seconds - adTimeRemaining) / currentAd.duration_seconds) * 100}%` }}
          />
        </div>
      </div>
    );
  };
  
  // Loading state - only for initial pre-roll check
  if (adLoading) {
    return (
      <div className="relative w-full aspect-video bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin mx-auto mb-2"></div>
          <p className="text-zinc-500 text-sm">Loading...</p>
        </div>
      </div>
    );
  }
  
  // PRE-ROLL: Replace content entirely (content hasn't started yet)
  if (isAdPlaying && adType === 'pre_roll' && currentAd) {
    return (
      <div className="relative w-full aspect-video bg-black">
        <AdOverlayUI />
      </div>
    );
  }
  
  // MID-ROLL + CONTENT: Overlay ad on top of content (children stay mounted)
  // This prevents YouTube iframes from restarting on 24/7 channels
  return (
    <div className="relative w-full">
      {children}
      {isAdPlaying && adType === 'mid_roll' && currentAd && <AdOverlayUI />}
    </div>
  );
};

export default VideoAdPlayer;
